import * as React from 'react';
import { useEffect, useRef } from 'react';
import { getConfig } from '../../../config';
import { logVideoPause, logVideoPlay } from '../../../utils/logger';
import Hls from 'hls.js';

type Props = {
    id: string;
    src: string;
};

const HlsVideo = ({ id, src }: Props) => {
    const videoRef = useRef(null);
    const observer = useRef(null);

    const onIntersection = (entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                videoRef.current.play();
            } else {
                videoRef.current.pause();
            }
        });
    };

    const { vertical, subvertical } = getConfig();

    useEffect(() => {
        if (Hls.isSupported()) {
            const hls = new Hls();
            hls.loadSource(src);
            hls.attachMedia(videoRef.current);
        } else if (
            videoRef.current.canPlayType('application/vnd.apple.mpegurl')
        ) {
            // If HLS isn't supported natively, but the video tag can play HLS,
            // just load the HLS source directly into the video tag.
            videoRef.current.src = src;
        }

        if ('IntersectionObserver' in window) {
            observer.current = new IntersectionObserver(onIntersection, {
                root: null, // viewport
                threshold: 0.5, // play the video when it's half in view
            });
            observer.current.observe(videoRef.current);
        } else {
            // IntersectionObserver is not supported, play the video anyway
            videoRef.current.play();
        }

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [src]);

    // sets event listeners to log on play/pause
    useEffect(() => {
        const video = videoRef.current;

        const handlePlay = () => {
            logVideoPlay(
                id,
                vertical,
                subvertical,
                video.currentTime,
                video.duration,
            );
        };

        const handlePause = () => {
            logVideoPause(
                id,
                vertical,
                subvertical,
                video.currentTime,
                video.duration,
            );
        };

        if (video) {
            video.addEventListener('play', handlePlay);
            video.addEventListener('pause', handlePause);
        }

        return () => {
            if (video) {
                video.removeEventListener('play', handlePlay);
                video.removeEventListener('pause', handlePause);
            }
        };
    }, [id, subvertical, vertical, videoRef]);

    return (
        <div>
            <video
                width="100%"
                height="400"
                ref={videoRef}
                muted={true}
                disablePictureInPicture
                autoPlay
                controls={true}
                controlsList={'nodownload noremoteplayback noplaybackrate'}
            />
        </div>
    );
};

export default HlsVideo;
